"use client"

import { useEffect, useRef, useState } from "react"
import { useLocale, useTranslations } from "next-intl"
import { ArrowRight, Loader2 } from "lucide-react"
import { BottomSheet, BottomSheetContent, BottomSheetDescription, BottomSheetTitle } from "@/components/ui/bottom-sheet"
import { BoothMark } from "@/components/ui/booth-mark"
import { Eyebrow, GlassCard, Reveal } from "@/components/glass/glass-kit"
import { HeroCtaButton } from "@/components/glass/hero-cta"
import { useUserRole } from "@/providers/user-role-provider"
import {
  CO_DEVELOP_INTERESTS,
  CO_DEVELOP_ROLES,
  type CoDevelopInterest,
  type CoDevelopRole,
  rolesHaveShop,
} from "@/lib/co-develop"
import { cn } from "@/lib/utils"

// "Help us shape BOOTH": an open invitation to build the product with us.
// Anyone can apply (barbers, owners, stylists, designers, engineers, artists,
// creators, entrepreneurs), so the form asks who you are first, as many as
// fit, and only asks about a shop when one of them works in one.
//
// The section sits on the glass landing and reads its --pg-* tokens. The form
// opens in the ink BottomSheet like every other visitor-facing commitment
// (inquire, sign up), so it is the one overlay shape on the page.

const CARDS = ["apply", "build"] as const

// Softer than the glass kit's defaults (a 60px card drop, a 40px button drop),
// which read heavy on short cards in a row, most of all in light mode.
// `!` because both components set their shadow inline.
const SOFT_CARD_SHADOW = "!shadow-[inset_0_1px_0_var(--pg-gloss),0_10px_24px_-20px_rgba(0,0,0,0.45)]"
const SOFT_BUTTON_SHADOW = "!shadow-[0_8px_18px_-14px_rgba(0,0,0,0.55)]"

export function CoDevelopSection() {
  const t = useTranslations("marketing.landing.coDevelop")
  const [open, setOpen] = useState(false)

  return (
    <section id="co-develop" className="scroll-mt-24 px-5 py-20 sm:py-28">
      <div className="mx-auto max-w-5xl">
        <Reveal>
          <div className="mb-10 max-w-xl sm:mb-12">
            <Eyebrow mark>{t("eyebrow")}</Eyebrow>
            <h2 className="mt-5 text-[clamp(2.15rem,4.8vw,3.35rem)] font-medium leading-[1.04] tracking-[-0.035em] text-balance">
              {t("heading")}
            </h2>
            <p
              className="mt-6 text-[15px] leading-relaxed text-pretty sm:mt-7 sm:text-base"
              style={{ color: "var(--pg-muted)" }}
            >
              {t("sub")}
            </p>
            <div className="mt-8">
              <HeroCtaButton onClick={() => setOpen(true)} icon={ArrowRight} className={cn("!w-auto", SOFT_BUTTON_SHADOW)}>
                {t("cta")}
              </HeroCtaButton>
            </div>
          </div>
        </Reveal>

        {/* Two steps, in order: apply, then co-develop. */}
        <div className="grid gap-3 sm:grid-cols-2">
          {CARDS.map((key, i) => (
            <Reveal key={key} delay={i * 60}>
              <GlassCard className={cn("h-full p-6", SOFT_CARD_SHADOW)}>
                {/* The landing's card heading and body (see Features), not
                    a spaced uppercase label: one type system on the page. */}
                <h3 className="text-[15px] font-semibold tracking-tight text-balance" style={{ color: "var(--pg-fg)" }}>
                  {/* <nb> keeps "co-development" whole: it broke at the
                      hyphen, leaving "co-" alone at the end of a line. */}
                  {t.rich(`cards.${key}.label`, {
                    nb: (chunks) => <span className="whitespace-nowrap">{chunks}</span>,
                  })}
                </h3>
                <p className="mt-2 text-[13px] leading-relaxed text-pretty" style={{ color: "var(--pg-muted)" }}>
                  {t(`cards.${key}.body`)}
                </p>
              </GlassCard>
            </Reveal>
          ))}
        </div>
      </div>

      <CoDevelopSheet open={open} onOpenChange={setOpen} />
    </section>
  )
}

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: { sitekey: string; callback: (token: string) => void }) => string
      reset: (id?: string) => void
    }
  }
}

const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY

// Fields on ink: black glass with no stroke. A top gloss and an inset light
// edge do the work a ring did, and focus lifts with a soft halo. 16px type on
// phones so iOS doesn't zoom.
const FIELD =
  "h-10 w-full min-w-0 rounded-xl px-3.5 text-base text-white outline-none transition-shadow [background:linear-gradient(180deg,rgba(255,255,255,0.07),rgba(255,255,255,0.015)_50%,rgba(255,255,255,0)),rgba(0,0,0,0.55)] shadow-[inset_0_1px_0_rgba(255,255,255,0.09),inset_0_-1px_0_rgba(0,0,0,0.45)] placeholder:text-white/30 focus:shadow-[inset_0_1px_0_rgba(255,255,255,0.14),0_0_0_3px_rgba(255,255,255,0.07)] md:text-sm"
const LABEL = "text-[12px] font-medium text-white/75"
const CHIP =
  "h-8 shrink-0 rounded-full px-3 text-[12px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
const CHIP_OFF = "bg-white/[0.06] text-white/75 hover:bg-white/[0.1]"
// One scrolling row on the phone, wrapping from sm: ten roles used to take
// three rows of the sheet.
const CHIP_ROW =
  "-mx-6 flex gap-1.5 overflow-x-auto px-6 pt-1 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 [&::-webkit-scrollbar]:hidden"
// The nav's Join pill, a step up in size for a form's primary action.
const PILL =
  "inline-flex h-9 items-center justify-center gap-1.5 rounded-full px-4 text-[13px] font-medium tracking-tight transition-transform duration-200 hover:scale-[1.03] active:scale-95 disabled:pointer-events-none disabled:opacity-60"
const PILL_STYLE = { background: "#FAFAF8", color: "#0A0A0A", boxShadow: "0 2px 12px rgba(0,0,0,0.28)" }

function Field({
  label,
  htmlFor,
  optional,
  children,
}: {
  label: string
  htmlFor?: string
  optional?: string
  children: React.ReactNode
}) {
  return (
    <div className="min-w-0 space-y-1.5">
      <label htmlFor={htmlFor} className={cn(LABEL, "flex gap-1.5")}>
        {label}
        {optional && <span className="font-normal text-white/40">{optional}</span>}
      </label>
      {children}
    </div>
  )
}

export function CoDevelopSheet({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const t = useTranslations("marketing.landing.coDevelop.form")
  const locale = useLocale()
  const { profile } = useUserRole()

  const [roles, setRoles] = useState<CoDevelopRole[]>([])
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [city, setCity] = useState("")
  const [shopName, setShopName] = useState("")
  const [links, setLinks] = useState("")
  const [interests, setInterests] = useState<CoDevelopInterest[]>([])
  const [message, setMessage] = useState("")
  const [honeypot, setHoneypot] = useState("")
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)
  const [token, setToken] = useState<string | null>(null)
  const openedAt = useRef(0)
  const widgetRef = useRef<HTMLDivElement>(null)

  // Signed in, fill what we already know. Never over something typed.
  useEffect(() => {
    if (!open) return
    openedAt.current = performance.now()
    if (profile?.full_name) setName((v) => v || profile.full_name!)
    if (profile?.email) setEmail((v) => v || profile.email!)
  }, [open, profile])

  // Turnstile only when a site key is configured; the honeypot and dwell
  // check carry the form otherwise (see lib/turnstile.ts).
  useEffect(() => {
    if (!open || sent || !SITE_KEY) return
    let cancelled = false
    const render = () => {
      if (cancelled || !widgetRef.current || !window.turnstile || widgetRef.current.childElementCount) return
      window.turnstile.render(widgetRef.current, { sitekey: SITE_KEY, callback: setToken })
    }
    if (window.turnstile) {
      // The sheet mounts its content a frame after open.
      requestAnimationFrame(render)
    } else {
      const script = document.createElement("script")
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
      script.async = true
      script.onload = render
      document.head.appendChild(script)
    }
    return () => {
      cancelled = true
    }
  }, [open, sent])

  const close = () => {
    if (sending) return
    onOpenChange(false)
    // Keep a half-filled form if they come back; clear it once it has gone.
    if (sent) {
      setSent(false)
      setRoles([])
      setCity("")
      setShopName("")
      setLinks("")
      setInterests([])
      setMessage("")
    }
    setError(null)
  }

  const toggleRole = (r: CoDevelopRole) =>
    setRoles((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]))
  const toggleInterest = (i: CoDevelopInterest) =>
    setInterests((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]))

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!roles.length || !name.trim() || !email.includes("@")) {
      setError(t("errorMissing"))
      return
    }
    setSending(true)
    try {
      const res = await fetch("/api/co-develop", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          roles,
          name,
          email,
          city,
          shopName,
          links,
          interests,
          message,
          locale,
          website: honeypot,
          elapsedMs: performance.now() - openedAt.current,
          turnstileToken: token,
        }),
      })
      if (!res.ok) {
        const json = (await res.json().catch(() => ({}))) as { error?: string }
        throw new Error(json.error === "missing" ? t("errorMissing") : t("errorSend"))
      }
      setSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errorSend"))
      if (SITE_KEY && window.turnstile) {
        window.turnstile.reset()
        setToken(null)
      }
    } finally {
      setSending(false)
    }
  }

  const firstName = name.trim().split(/\s+/)[0] || name

  return (
    <BottomSheet open={open} onOpenChange={(o) => !o && close()}>
      {/* z-[80]: above the landing's floating nav (z-[70]), which otherwise
          sat over the sheet's title on phones. Safe here because this form has
          no Radix popovers (they portal at z-50 and would land underneath). */}
      <BottomSheetContent
        tone="ink"
        closeDisabled={sending}
        className="z-[80] gap-0 sm:max-w-[480px]"
        overlayClassName="z-[80]"
      >
        {sent ? (
          <div className="flex flex-col items-center px-6 pb-8 pt-6 text-center">
            <BoothMark size={44} className="text-white" />
            <BottomSheetTitle className="mt-4 text-[17px] font-medium tracking-tight">{t("successTitle")}</BottomSheetTitle>
            <BottomSheetDescription className="mt-1.5 max-w-[34ch] text-[13px] leading-relaxed text-white/55 text-pretty">
              {t("successBody", { name: firstName, email })}
            </BottomSheetDescription>
            <button type="button" onClick={close} className={cn(PILL, "mt-6")} style={PILL_STYLE}>
              {t("done")}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col" noValidate>
            <div className="flex-none px-6 pb-3 pt-1 pr-14">
              <BottomSheetTitle className="text-[17px] font-medium tracking-tight">{t("title")}</BottomSheetTitle>
              <BottomSheetDescription className="mt-1 text-[13px] leading-relaxed text-white/55 text-pretty">
                {t("description")}
              </BottomSheetDescription>
            </div>

            <div data-sheet-scroll className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-6 pb-6">
              <fieldset className="space-y-2">
                <legend className={cn(LABEL, "flex gap-1.5")}>
                  {t("iAm")}
                  <span className="font-normal text-white/40">{t("pickAll")}</span>
                </legend>
                <div className={CHIP_ROW}>
                  {CO_DEVELOP_ROLES.map((r) => {
                    const on = roles.includes(r)
                    return (
                      <button
                        key={r}
                        type="button"
                        aria-pressed={on}
                        onClick={() => toggleRole(r)}
                        className={cn(
                          CHIP,
                          on ? "bg-white text-[#0e0e0e]" : CHIP_OFF,
                        )}
                      >
                        {t(`roles.${r}`)}
                      </button>
                    )
                  })}
                </div>
              </fieldset>

              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={t("name")} htmlFor="codev-name">
                  <input id="codev-name" className={FIELD} autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
                </Field>
                <Field label={t("email")} htmlFor="codev-email">
                  <input
                    id="codev-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    className={FIELD}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Field>
                <Field label={t("city")} htmlFor="codev-city" optional={t("optional")}>
                  <input id="codev-city" className={FIELD} autoComplete="address-level2" value={city} onChange={(e) => setCity(e.target.value)} />
                </Field>
                {rolesHaveShop(roles) && (
                  <Field label={t("shopName")} htmlFor="codev-shop" optional={t("optional")}>
                    <input id="codev-shop" className={FIELD} value={shopName} onChange={(e) => setShopName(e.target.value)} />
                  </Field>
                )}
              </div>

              <fieldset className="space-y-2">
                <legend className={LABEL}>{t("helpWith")}</legend>
                <div className={CHIP_ROW}>
                  {CO_DEVELOP_INTERESTS.map((i) => {
                    const on = interests.includes(i)
                    return (
                      <button
                        key={i}
                        type="button"
                        aria-pressed={on}
                        onClick={() => toggleInterest(i)}
                        className={cn(
                          CHIP,
                          on ? "bg-white/[0.2] text-white" : CHIP_OFF,
                        )}
                      >
                        {t(`interests.${i}`)}
                      </button>
                    )
                  })}
                </div>
              </fieldset>

              <Field label={t("links")} htmlFor="codev-links" optional={t("optional")}>
                <input
                  id="codev-links"
                  className={FIELD}
                  placeholder={t("linksPlaceholder")}
                  value={links}
                  onChange={(e) => setLinks(e.target.value)}
                />
              </Field>

              <Field label={t("message")} htmlFor="codev-message" optional={t("optional")}>
                <textarea
                  id="codev-message"
                  rows={2}
                  className={cn(FIELD, "h-auto min-h-20 resize-none py-2.5")}
                  placeholder={t("messagePlaceholder")}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </Field>

              {/* Honeypot: off-screen, out of the tab order, ignored by humans. */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                className="absolute -left-[9999px] h-px w-px opacity-0"
              />
              {SITE_KEY && <div ref={widgetRef} />}
            </div>

            {/* The button bar, faded into the body rather than ruled off. */}
            <div className="relative flex flex-none flex-col gap-2 bg-booth-ink px-6 pb-5 pt-3 before:pointer-events-none before:absolute before:inset-x-0 before:-top-6 before:h-6 before:bg-gradient-to-t before:from-booth-ink before:to-transparent">
              {error && (
                <p role="alert" className="text-pretty text-center text-[13px] text-red-300">
                  {error}
                </p>
              )}
              <div className="flex items-center justify-end gap-1">
                <button
                  type="button"
                  onClick={close}
                  disabled={sending}
                  className="h-9 rounded-full px-3 text-[13px] font-medium text-white/55 transition-colors hover:text-white"
                >
                  {t("cancel")}
                </button>
                <button type="submit" disabled={sending} className={PILL} style={PILL_STYLE}>
                  {sending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {t("submit")}
                </button>
              </div>
            </div>
          </form>
        )}
      </BottomSheetContent>
    </BottomSheet>
  )
}
